<template>
  <div v-if="data">
    <!-- <pre>{{ data }}</pre> -->
    <ContentRenderer :value="data">
      <section
        class="w-4/6 pt-12 mx-auto text-secondary transition-colors duration-800 xl:w-11/12 sm:w-full"
        v-colors.dark="{ primary: '#7939FF', secondary: '#FBD302' }"
      >
        <img :src="data.image" :alt="data.title" class="mx-auto w-full" />
      </section>
      <section
        class="w-4/6 max-w-2xl pt-12 mx-auto text-secondary transition-colors duration-800 xl:w-11/12 sm:w-full"
        v-colors="{ primary: '#ffffff', secondary: '#7939FF' }"
      >
        <p class="blog-date opacity-50 mb-4">{{ formatDate(data.date) }}</p>
        <ContentRendererMarkdown :value="data" />
      </section>
      <template #empty>
        <p>Blogpost wurde nicht gefunden.</p>
        <NuxtLink
          :to="localePath({ name: 'index' })"
          class="inline-block py-3 px-6 mt-4 bg-purple/10 text-purple rounded-s text-sm no-underline sm:w-full sm:text-center"
          >{{ $t('error.cta') }}</NuxtLink
        >
      </template>
    </ContentRenderer>
  </div>
  <!-- <ContentDoc /> -->
</template>

<script setup lang="ts">
// state + data
const { locale, t } = useI18n();
const localePath = useLocalePath();
const { frontendBaseUrl } = useRuntimeConfig().public;
const { path } = useRoute();
const { data } = await useAsyncData(path, () => queryContent(path).findOne());
const slugs = computed(() => {
  if (data.value) {
    return data.value.slug;
  }
});
const setI18nParams = useSetI18nParams();
// functionality
function formatDate(date: string) {
  return new Intl.DateTimeFormat(locale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date));
}

// page meta
useSeoMeta({
  title: data.value?.seoTitle || t('seo-meta.fallback.title'),
  ogTitle: data.value?.seoTitle || t('seo-meta.fallback.title'),
  description: data.value?.seoDescription || t('seo-meta.fallback.description'),
  ogDescription: data.value?.seoDescription || t('seo-meta.fallback.description'),
  ogImage: {
    url:
      `${frontendBaseUrl}${data.value?.image}` || `${frontendBaseUrl}/img/og/thg-og-home-web.jpg`,
    type: 'image/jpeg',
    width: undefined,
    height: undefined,
    alt: data.value?.seoTitle || t('seo-meta.fallback.title'),
  },
  ogUrl: `${frontendBaseUrl}${path}`,
  ogType: 'article',
  ogLocale: locale.value ?? 'de',
  ogLocaleAlternate: ['en', 'de', 'fr', 'it'].filter((lang) => lang !== locale.value),
  twitterCard: 'summary_large_image',
});
setI18nParams({
  de: { slug: slugs.value.de },
  en: { slug: slugs.value.en },
  fr: { slug: slugs.value.fr },
  it: { slug: slugs.value.it },
});
</script>

<style scoped>
:deep(h1) {
  @apply text-3xl md:text-2xl sm:text-xl leading-tight mb-4;
}
:deep(h2) {
  @apply text-xl sm:text-lg leading-tight mt-16 mb-4 no-underline text-balance;
}
:deep(p) {
  @apply mb-4 text-base sm:text-sm;
}
:deep(p:not(.blog-date)) {
  @apply text-black;
}
:deep(p:first-of-type) {
  @apply text-lg;
}
/* :deep(strong:first-of-type) {
  color: var(--secondary-section-color, #000);
  transition: color 0.8s;
} */
:deep(a[role='button']) {
  @apply bg-orange text-white text-center text-balance transition-colors no-underline inline-flex h-12 md:h-auto items-center justify-center px-8 md:py-4 mt-8 rounded-s;
}
:deep(a[role='button']:hover) {
  @apply bg-yellow text-purple;
}
:deep(ul) {
  @apply list-disc list-inside text-base sm:text-sm text-black;
}
</style>
